import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BG_META } from "@/components/status";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { UserCircle, Loader2, ShieldCheck, CreditCard, Star } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: user?.name || "", phone: user?.phone || "", bio: user?.bio || "",
    service_area: user?.service_area || "", avatar: user?.avatar || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/auth/profile", form);
      toast.success("Profile updated");
      await refreshUser();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const isCleaner = user?.role === "cleaner";
  const initials = (form.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="space-y-6 max-w-3xl" data-testid="profile-page">
      <div>
        <h1 className="font-heading font-black text-3xl tracking-tight text-sapphire">My Profile</h1>
        <p className="text-muted-foreground text-sm mt-1">Manage how you appear on Clean Slate Connect.</p>
      </div>

      <div className="flex items-center gap-4 border border-border rounded-md bg-white p-5">
        {form.avatar ? (
          <img src={form.avatar} alt="" className="w-16 h-16 rounded-md object-cover border border-border" />
        ) : (
          <div className="w-16 h-16 rounded-md bg-ocean text-white flex items-center justify-center text-xl font-bold font-heading">{initials}</div>
        )}
        <div>
          <div className="font-heading font-bold text-lg text-sapphire">{form.name}</div>
          <div className="text-sm text-muted-foreground">{user?.email} · <span className="capitalize">{user?.role === "pm" ? "Host" : user?.role}</span></div>
        </div>
      </div>

      {isCleaner && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="border border-border rounded-md bg-white p-4 flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm"><ShieldCheck className="w-4 h-4 text-ocean" /> Background Check</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${BG_META[user.background_check_status]?.cls}`}>{BG_META[user.background_check_status]?.label}</span>
          </div>
          <div className="border border-border rounded-md bg-white p-4 flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm"><CreditCard className="w-4 h-4 text-ocean" /> Stripe Payouts</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${user.stripe_onboarded ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>{user.stripe_onboarded ? "Connected" : "Not connected"}</span>
          </div>
        </div>
      )}

      <form onSubmit={save} className="border border-border rounded-md bg-white p-6 space-y-4" data-testid="profile-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Full Name</Label>
            <Input required value={form.name} onChange={set("name")} data-testid="profile-name" />
          </div>
          <div>
            <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Phone</Label>
            <Input value={form.phone} onChange={set("phone")} data-testid="profile-phone" />
          </div>
        </div>
        {isCleaner && (
          <div>
            <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Service Area</Label>
            <Input value={form.service_area} onChange={set("service_area")} placeholder="e.g. Austin, TX" data-testid="profile-service-area" />
          </div>
        )}
        <div>
          <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Avatar Image URL</Label>
          <Input value={form.avatar} onChange={set("avatar")} placeholder="https://…" data-testid="profile-avatar" />
        </div>
        <div>
          <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">{isCleaner ? "About You" : "Company / Bio"}</Label>
          <Textarea value={form.bio} onChange={set("bio")} placeholder={isCleaner ? "Tell hosts about your experience…" : "Tell cleaners about your properties…"} data-testid="profile-bio" />
        </div>
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={saving} className="bg-ocean hover:bg-ocean/90" data-testid="profile-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
          </Button>
          {isCleaner && (
            <Button type="button" variant="outline" onClick={() => navigate(`/profile/${user.id}`)} data-testid="view-public-profile">
              <Star className="w-4 h-4 mr-1" /> View Public Profile
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
